import { SHARED_POST_GONE } from "../livingRoom.js";
import { Avatar } from "./LivingRoomShared";
import type { Identity } from "./LivingRoomShared";

// HER QC (2026-08-07) — **A SHARED POST IS A CARD, NEVER A RAW PATH.**
//
// Her screenshot is the spec of the failure: a bubble reading `Shared a post: TITLE` followed by
// `/living-room?post=1406` — *"a raw path, nothing clickable, nothing shown. Unacceptable."*
//
// **HOOKLESS, so `scripts/shared-post-cards.mjs` CALLS it** and reads the tree back — including the
// case that matters most and that a screenshot of a working card can never show: what it draws when
// the post is gone.
//
// ---------------------------------------------------------------------------------------------
// HER QC ROUND TWO (2026-08-07) — **COMPACT, AND THE PICTURE AREA CANNOT GHOST.**
//
// Round one shipped a card that rendered her post's art at ENORMOUS size, forced a horizontal
// scrollbar across the whole thread, and drew a broken-image ghost in its corner. Both causes were
// found in the code rather than guessed at, and both are repaired STRUCTURALLY rather than by
// tuning a number:
//
//   1. **THE ENORMOUS ART WAS THE AVATAR, NOT THE THUMBNAIL.** Round one widened `Avatar`'s `size`
//      prop to accept `"xs"` and **never added `.lr-avatar--xs` to the stylesheet** — and
//      `.lr-avatar` itself carries no width or height, only `--md` and `--sm` do. So the span had NO
//      SIZE and `.lr-avatar__img { width: 100% }` resolved against nothing: the browser laid the
//      profile photo out at its INTRINSIC resolution and the flex row grew to fit it. A size token
//      added in a TYPE with no declaration behind it has no error state — it renders, at whatever
//      size the bytes happen to be. The repair is at the ladder: `--xs` exists, and `.lr-avatar`
//      carries a DEFAULT size so an unrostered token can never again resolve to "no size at all".
//
//   2. **THE GHOST WAS A VIDEO IN AN `<img>`.** The store took the first attachment regardless of
//      kind, so her video share handed video bytes to an image element. The kind now travels with
//      the id (`sharedThumb`), and **THIS COMPONENT RENDERS NO `<img>` AT ALL** — an image thumbnail
//      is a CSS `background-image`, which draws NOTHING when its bytes fail rather than a broken
//      icon, in every browser, with no error handler and no hook to lose. That is the ghost killed
//      by construction rather than by an `onError` somebody can delete.
//
// **THE SIZE LAW IS A NAMED CONSTANT AND THE CSS READS IT.** Her band is "roughly 80 to 120px tall";
// the declaration is `--shared-thumb-h` in the stylesheet and this file states the band it must sit
// in so the fixture can hold both ends of it.
// ---------------------------------------------------------------------------------------------

export type SharedThumb = { url: string; kind: string };
export type SharedPost = {
  id: string;
  gone?: boolean;
  title?: string;
  excerpt?: string;
  kind?: string;
  author?: Identity;
  thumb?: SharedThumb | null;
};

/**
 * **A URL THAT COULD BREAK OUT OF `url(...)` IS NOT A URL.** The server composes
 * `/api/living-room/attachment/<id>` from a numeric id, so this can never fire today — and it is
 * here because the ONE place this component interpolates a value into CSS is the one place a stored
 * string could ever become something other than a source. Refusing it draws no picture area, which
 * is the same honest answer a text post gets.
 */
export function safeThumbUrl(url: string | undefined | null): string {
  const s = String(url || "");
  if (!s || !s.startsWith("/")) return "";
  return /["'()\\\s]/.test(s) ? "" : s;
}

export function SharedPostCard({ post, onOpen }: { post: SharedPost | null; onOpen: (id: string) => void }) {
  if (!post) return null;
  // **HER CLAUSE 2's OTHER HALF, AND IT IS NOT A DEAD LINK.** A post that is deleted — or that this
  // reader may not see, which the server deliberately does not distinguish — draws a warm sentence
  // and NOTHING TAPPABLE. A card that still looked tappable would be the raw path's defect again,
  // one layer up: something that promises to open and does not.
  if (post.gone) {
    return (
      <p className="lr-shared lr-shared--gone" data-shared-post={post.id}>{SHARED_POST_GONE}</p>
    );
  }
  const url = safeThumbUrl(post.thumb?.url);
  const kind = url ? String(post.thumb?.kind || "") : "";
  return (
    <button
      type="button"
      className="lr-shared"
      data-shared-post={post.id}
      onClick={() => onOpen(post.id)}
      // The whole card is the tap target — her words, *"tapping anywhere on it opens the actual
      // post"* — so it is one `<button>` rather than a div with a link somewhere inside it.
      aria-label={`Open the post: ${post.title || "shared post"}`}
    >
      {/* HER CLAUSE 3 — **NO SOURCE, NO ELEMENT.** A text post renders no picture area whatsoever
          rather than an empty box waiting for bytes that are never coming. */}
      {kind === "image" ? (
        <span
          className="lr-shared__shot"
          data-shot="image"
          // A background, never an `<img>`: bytes that fail draw nothing at all.
          style={{ backgroundImage: `url(${url})` }}
        />
      ) : kind === "video" ? (
        <span className="lr-shared__shot lr-shared__shot--video" data-shot="video">
          {/* **THE COVER FRAME IS THE VIDEO'S OWN FIRST FRAME, WHICH IS WHAT SHE ASKED FOR AND WHAT
              THIS SITE ALREADY HAS.** Nothing stores a poster image — `lr_attachments` has no cover
              column and this house has never extracted a frame — so inventing one would mean a
              migration, a job and a second set of bytes. `preload="metadata"` on the SAME gated
              route paints the real first frame for free. It is `muted`, `playsInline` and carries no
              controls, so it can never play here; `pointer-events` are off in the sheet so the tap
              still belongs to the card. Bytes that fail leave a dark panel with the play badge on
              it — a correct, compact video affordance, and never a broken-image icon. */}
          <video src={url} muted playsInline preload="metadata" tabIndex={-1} aria-hidden="true" />
          <span className="lr-shared__play" aria-hidden="true" />
        </span>
      ) : null}
      <span className="lr-shared__main">
        <span className="lr-shared__who">
          {post.author ? <Avatar who={post.author} size="xs" /> : null}
          <span className="lr-shared__name">@{post.author?.name || "the room"}</span>
        </span>
        <span className="lr-shared__title">{post.title}</span>
        {/* The first line or two only. A card that carried the whole post would BE the post, and the
            tap would have nothing left to do. */}
        {post.excerpt ? <span className="lr-shared__excerpt">{post.excerpt}</span> : null}
      </span>
    </button>
  );
}
